import { Link, useParams } from "react-router-dom";
import { useFunds } from "../lib/useFunds.js";
import { label as taxonomyLabel } from "@shared/taxonomy.js";
import {
  fundTypeLabel, ticketText as ticket, ticketLabel, ticketBadge, fundCta,
  tomTatDanhMucText, formatMoney, ketQuaLabel, thoiDiemVong, quyTuongTu, fundTypeKey,
} from "../lib/fundLabels.js";
import { readWorkspace, writeWorkspace, datDauXoa } from "../lib/workspace.js";
import { themMuc, boMuc, coTrongDanhSach, GOI_Y_TOI_DA } from "@shared/danhSachMucTieu.js";
import { tomTatDanhMuc } from "../lib/fundModel.js";
import NotFound from "./NotFound.jsx";
import LogoQuy from "../components/LogoQuy.jsx";
import { logoQuy } from "../data/fundLogos.js";
import { useEffect, useState } from "react";
import { track } from "../lib/track.js";

/** Rút gọn danh sách dài: giữ vài mục đầu, phần còn lại gộp thành "+n". */
function gon(domain, values, giu, ten) {
  const list = (values || []).map((v) => taxonomyLabel(domain, v) || v).filter(Boolean);
  if (!list.length) return null;
  const dau = list.slice(0, giu).join(", ");
  return list.length > giu ? `${dau} và ${list.length - giu} ${ten} khác` : dau;
}

/** Nối danh sách thành "A, B hoặc C". */
function hoac(list) {
  return list.length > 1 ? `${list.slice(0, -1).join(", ")} hoặc ${list[list.length - 1]}` : list[0] || "";
}
function nhan(domain, values) {
  return (values || []).map((v) => taxonomyLabel(domain, v) || v).filter(Boolean);
}

/**
 * "Phù hợp nếu bạn...": câu trả lời ngắn ở đầu trang, ghép từ đúng những gì đơn
 * vị đã công bố (giai đoạn, ngành, địa bàn, khoản tiền). Mục nào chưa công bố thì
 * nói rõ là chưa công bố, không suy đoán. Luôn cùng thứ tự ở mọi trang quỹ để
 * người đọc so được sau khi xem vài quỹ.
 */
function PhuHop({ fund }) {
  const th = fund.thesis || {};
  const gd = nhan("STAGE", th.stages);
  const ng = nhan("INDUSTRY", th.industries);
  const db = nhan("GEO", th.geos);
  const tien = ticket(fund);
  const coTien = tien !== "Chưa công bố";
  const dieuKien = th.hardRequirements?.length || 0;
  const dong = [
    gd.length ? { co: true, k: "Đang gọi vốn vòng", v: hoac(gd) } : { co: false, k: "Giai đoạn:", v: "không công bố vòng cụ thể" },
    ng.length
      ? { co: true, k: "Làm trong lĩnh vực", v: ng.length > 4 ? `${ng.slice(0, 3).join(", ")} hoặc ${ng.length - 3} ngành khác` : hoac(ng) }
      : { co: false, k: "Ngành:", v: "chưa công bố, có thể đầu tư đa ngành" },
    db.length ? { co: true, k: "Hoạt động tại", v: hoac(db) } : { co: false, k: "Địa bàn:", v: "chưa công bố" },
    coTien ? { co: true, k: `Cần khoảng (${ticketLabel(fund).toLowerCase()})`, v: tien } : { co: false, k: `${ticketLabel(fund)}:`, v: "chưa công bố" },
  ];
  return <section className="fd-phu-hop" aria-labelledby="fd-phu-hop-tieu-de">
    <h2 id="fd-phu-hop-tieu-de">Phù hợp nếu bạn</h2>
    <ul>
      {dong.map((d) => <li key={d.k} className={d.co ? "" : "chua"}>
        <span className="fd-dau" aria-hidden="true">{d.co
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 12h12" /></svg>}</span>
        <span><span className="fd-k">{d.k}</span> <strong>{d.v}</strong>{d.co && d.k.startsWith("Cần") && ticketBadge(fund) ? <span className="fd-ghi">{ticketBadge(fund)}</span> : null}</span>
      </li>)}
    </ul>
    {dieuKien > 0 && <p className="fd-dieu-kien">Và đáp ứng {dieuKien} điều kiện bắt buộc. <a href="#dieu-kien">Xem điều kiện ↓</a></p>}
  </section>;
}

/** Khối hành động cạnh "Phù hợp nếu bạn": nộp hồ sơ, lưu mục tiêu, đối chiếu. */
function HanhDong({ fund }) {
  const cta = fundCta(fund);
  const [daLuu, setDaLuu] = useState(false);
  const [tin, setTin] = useState("");
  useEffect(() => { try { setDaLuu(coTrongDanhSach(readWorkspace().saved, fund.fundId)); } catch { /* bỏ qua */ } }, [fund.fundId]);
  function luu() {
    try {
      const d = readWorkspace();
      const co = coTrongDanhSach(d.saved, fund.fundId);
      const moi = co
        ? { ...datDauXoa(d, `saved:${fund.fundId}`), saved: boMuc(d.saved, fund.fundId) }
        : { ...d, saved: themMuc(d.saved, { id: fund.fundId, name: fund.name }) };
      writeWorkspace(moi);
      setDaLuu(!co);
      if (!co) track("funding_source_save", { fund_id: fund.fundId });
      setTin(co ? "Đã bỏ khỏi danh sách mục tiêu." : `Đã thêm vào danh sách mục tiêu (${moi.saved.length}/${GOI_Y_TOI_DA}).`);
    } catch (e) {
      setTin(e?.message?.includes("Bỏ bớt") ? e.message : "Chưa lưu được. Kiểm tra bộ nhớ trình duyệt.");
    }
  }
  const ngay = fund.provenance?.lastVerifiedAt;
  return <aside className="fd-hanh-dong" aria-label="Bước tiếp theo">
    {cta
      ? <a className="nut" href={cta.href} onClick={() => track("funding_cta_click", { fund_id: fund.fundId, kind: cta.kind })} {...(cta.external ? { target: "_blank", rel: "noreferrer" } : {})}>{cta.label}</a>
      : fund.website ? <a className="nut" href={fund.website} target="_blank" rel="noreferrer" onClick={() => track("funding_cta_click", { fund_id: fund.fundId, kind: "website" })}>Trang chính thức ↗</a> : null}
    <button type="button" className={"nut phu" + (daLuu ? " bat" : "")} aria-pressed={daLuu} onClick={luu}>
      {daLuu ? "Đã trong mục tiêu ✓" : "Lưu vào danh sách mục tiêu"}
    </button>
    <Link className="fd-doi-chieu" to="/cong-cu/quy-phu-hop">Đối chiếu với startup của tôi →</Link>
    <p role="status" className="fd-tin">{tin}</p>
    <p className="fd-kiem-chung">{ngay ? `Kiểm chứng với nguồn chính thức ngày ${ngay.split("-").reverse().join("/")}` : "Chưa xác minh với nguồn chính thức"}</p>
  </aside>;
}

/** Ba quỹ gần nhất theo giai đoạn, ngành, loại: để người đọc đi tiếp thay vì rời trang. */
function QuyTuongTu({ fund, all }) {
  const ds = quyTuongTu(fund, all, 3);
  if (!ds.length) return null;
  const gd = fund.thesis?.stages?.[0];
  return <section className="fd-tuong-tu" aria-labelledby="fd-tuong-tu-tieu-de">
    <h2 id="fd-tuong-tu-tieu-de">Nguồn vốn tương tự</h2>
    <div className="fd-tuong-tu-luoi">
      {ds.map((g) => <Link key={g.fundId} to={`/nguon-von/${g.slug}`} className="fd-tt" onClick={() => track("funding_similar_click", { fund_id: g.fundId })}>
        <LogoQuy f={g} className="nvt-logo fd-tt-logo" />
        <span className="fd-tt-chu">
          <strong>{String(g.name).replace(/\s*—\s*/g, ": ")}</strong>
          <span className="fd-tt-meta">{fundTypeLabel(g)}</span>
          <span className="fd-tt-meta">{nhan("STAGE", g.thesis?.stages).join(", ") || "Chưa công bố vòng cụ thể"}</span>
          {ticket(g) !== "Chưa công bố" && <span className="fd-tt-tien">{ticket(g)}</span>}
        </span>
      </Link>)}
    </div>
    <p className="fd-tuong-tu-them">
      <Link to={gd ? `/nguon-von?stage=${gd}&xem=bang` : "/nguon-von?xem=bang"}>
        {gd ? `Xem mọi nguồn vốn vòng ${taxonomyLabel("STAGE", gd)} dạng bảng →` : "Xem toàn bộ nguồn vốn dạng bảng →"}
      </Link>
    </p>
  </section>;
}

/**
 * Khối "Danh mục đã đầu tư" — bằng chứng đi kèm phần "Quỹ tìm kiếm".
 *
 * Dòng tóm tắt ở đầu khối được SUY TỪ chính danh sách bên dưới (tomTatDanhMuc),
 * không phải câu viết tay, nên không bao giờ nói khác với dữ liệu.
 *
 * Con số vòng gọi vốn luôn kèm nguồn và luôn có câu đính chính ngay dưới danh
 * sách: đó là tổng vòng, không phải séc của quỹ này. Đọc nhầm chỗ này là dựng
 * sai mỏ neo định giá cho chính vòng gọi vốn của người đọc.
 */
function DanhMucDauTu({ fund }) {
  const dm = fund.portfolio;
  if (!dm?.items?.length) return null;
  const tomTat = tomTatDanhMuc(fund);
  const cauTomTat = tomTatDanhMucText(fund, tomTat);
  const coSo = dm.items.some((it) => it.roundSize);
  return <section className="dm-khoi">
    <h2>Danh mục đã đầu tư</h2>
    {cauTomTat && <p className="dm-tom-tat">{cauTomTat}</p>}
    <ul className="dm-ds">
      {dm.items.map((it, i) => {
        const vong = [it.stage ? taxonomyLabel("STAGE", it.stage) : "", thoiDiemVong(it.roundDate)]
          .filter(Boolean).join(", ");
        const meta = [
          gon("INDUSTRY", it.industries, 2, "ngành"),
          it.geo ? taxonomyLabel("GEO", it.geo) : "",
          it.leadInvestor === true ? "Quỹ dẫn vòng" : "",
        ].filter(Boolean).join(", ");
        return <li key={`${it.name}-${i}`}>
          <div className="dm-dau">
            <h3>{it.url
              ? <a href={it.url} target="_blank" rel="noreferrer">{it.name}</a>
              : it.name}</h3>
            {vong && <span className="dm-vong">{vong}</span>}
          </div>
          {it.whatTheyDo && <p className="dm-lam">{it.whatTheyDo}</p>}
          {meta && <p className="dm-meta">{meta}</p>}
          {it.outcome && <p className="dm-ket-qua">
            <span className="dm-chip">{ketQuaLabel(it.outcome)}</span>
            {it.outcomeNote ? ` ${it.outcomeNote}` : ""}
          </p>}
          {it.roundSize && <p className="dm-so">
            Vòng công bố {formatMoney(it.roundSize)}
            {" "}<a href={it.roundSource} target="_blank" rel="noreferrer">nguồn</a>
          </p>}
        </li>;
      })}
    </ul>
    {coSo && <p className="dm-chu-thich">
      Số tiền ở trên là <strong>tổng vòng gọi vốn được công bố</strong>, gồm cả phần của
      các nhà đầu tư khác cùng tham gia, không phải séc riêng của quỹ này. Đừng dùng nó
      để suy ra quỹ sẽ đầu tư bao nhiêu vào bạn.
    </p>}
    <p className="dm-chu-thich">
      Danh mục chỉ liệt kê những doanh nghiệp được nêu công khai, không phải toàn bộ khoản
      đầu tư của quỹ.{dm.note ? ` ${dm.note}` : ""}
      {dm.verifiedAt ? ` Kiểm chứng ngày ${dm.verifiedAt}.` : ""}
      {dm.source ? <> <a href={dm.source} target="_blank" rel="noreferrer">Trang danh mục của quỹ ↗</a></> : null}
    </p>
  </section>;
}

/** Trang chi tiết indexable: giữ bằng chứng và điều kiện tách khỏi điểm khớp. */
export default function FundDetail() {
  const { slug } = useParams();
  const { funds } = useFunds();
  const all = funds.filter((f) => f.publish?.status === "PUBLISHED" && !f.isExample);
  const fund = all.find((f) => f.slug === slug);
  const fundId = fund?.fundId;
  useEffect(() => {
    if (fundId) track("funding_source_view", { fund_id: fundId });
  }, [fundId]);
  if (!fund) return <NotFound />;
  return <article className="van-ban fund-detail">
    <p className="ngay"><Link to="/nguon-von">Nguồn vốn cho startup</Link> / {fundTypeLabel(fund)}</p>
    {logoQuy(fund.slug) ? (
      <div className="fund-detail-dau">
        <LogoQuy f={fund} className="nvq-o fund-detail-logo" />
        <h1>{fund.name}</h1>
      </div>
    ) : <h1>{fund.name}</h1>}
    <p className="tom-tat">{fund.content?.summary || "Thông tin được tổng hợp từ nguồn công khai của đơn vị."}</p>
    {/* Trả lời trước: ai hợp với quỹ này và bấm vào đâu để đi tiếp. */}
    <div className="fd-tra-loi">
      <PhuHop fund={fund} />
      <HanhDong fund={fund} />
    </div>
    <div className="notice">Thông tin này không phải lời mời đầu tư. Hãy kiểm tra lại điều kiện và kênh nộp hồ sơ trên trang chính thức trước khi gửi deck.</div>
    {fund.content?.lookingFor?.length > 0 && <section><h2>Quỹ tìm kiếm</h2><ul>{fund.content.lookingFor.map((x) => <li key={x}>{x}</li>)}</ul></section>}
    <DanhMucDauTu fund={fund} />
    <section id="dieu-kien"><h2>Điều kiện bắt buộc đã công bố</h2>{fund.thesis?.hardRequirements?.length ? <ul>{fund.thesis.hardRequirements.map((x, i) => <li key={i}>{x.label}</li>)}</ul> : <p>Chưa có điều kiện riêng được ghi trong danh mục. Điều này không đồng nghĩa là không có điều kiện; cần xác minh trực tiếp.</p>}</section>
    {fund.content?.tips?.length > 0 && <section><h2>Lưu ý khi chuẩn bị hồ sơ</h2><ul>{fund.content.tips.map((x) => <li key={x}>{x}</li>)}</ul></section>}
    <section><h2>Nguồn và ngày kiểm chứng</h2><p>Kiểm chứng gần nhất: {fund.provenance?.lastVerifiedAt || "Chưa công bố"}.</p><ul>{(fund.provenance?.sources || []).map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer">Nguồn chính thức</a>{s.note ? `: ${s.note}` : ""}</li>)}</ul></section>
    <QuyTuongTu fund={fund} all={all} />
  </article>;
}
